import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Input,
  Button,
  Form,
  Space,
  Typography,
  Avatar,
  message,
  Divider,
  AutoComplete,
  Card,
  Row,
  Col,
  Select,
  Collapse,
  Tag,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  SaveOutlined,
  EditOutlined,
  PlusOutlined,
  DownOutlined,
  UpOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import { Customer } from '../types';
import { customerApi } from '../services/api';
import { useDebounce } from '../hooks';
import { useEstimateFormContext } from '../contexts/EstimateFormContext';
import { AIFieldConfidenceBadge } from './AIFieldConfidenceBadge';
import type { CustomerMatch } from '../utils/customerMatcher';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = AutoComplete;

interface CustomerInlineFormProps {
  value?: Customer;
  onChange?: (customer: Customer) => void;
  disabled?: boolean;
  /** List of AI-matched customer candidates — shown as a recommendation list. */
  aiCustomerCandidates?: CustomerMatch[];
  /** Called when user picks a candidate from the list. */
  onAcceptCandidate?: (customer: Customer) => void;
  /** Called when user dismisses the list (wants to create new instead). */
  onDismissCandidates?: () => void;
}

const CustomerInlineForm: React.FC<CustomerInlineFormProps> = ({
  value,
  onChange,
  disabled = false,
  aiCustomerCandidates = [],
  onAcceptCandidate,
  onDismissCandidates,
}) => {
  const [form] = Form.useForm();
  const formContext = useEstimateFormContext();
  const [isFormMounted, setIsFormMounted] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(value || null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showFormAsTyping, setShowFormAsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Debounce for auto-select logic only (filtering is instant via getSearchOptions)
  const debouncedSearchValue = useDebounce(searchValue, 150);

  // Guard: track the last customerFieldsApproved we applied so we don't
  // re-run form.setFieldsValue on every context re-render.
  const appliedAiDataRef = useRef<Record<string, string> | null>(null);

  // ── AI confidence helpers ──
  // Look up confidence info for a specific customer field path
  const fieldConf = (fieldName: string) =>
    formContext.fieldConfidences[`customer.${fieldName}`];

  // Confidence-based border styling for Input components
  const confStyle = (fieldName: string): React.CSSProperties => {
    const fc = fieldConf(fieldName);
    if (!fc) return {};
    const shadows: Record<string, string> = {
      blue: '0 0 0 2px rgba(82, 196, 26, 0.3)',
      yellow: '0 0 0 2px rgba(250, 173, 20, 0.4)',
      red: '0 0 0 2px rgba(255, 77, 79, 0.4)',
    };
    return { boxShadow: shadows[fc.band] ?? '' };
  };

  // Ant Design Input status for yellow/red bands
  const confStatus = (fieldName: string): 'warning' | 'error' | undefined => {
    const fc = fieldConf(fieldName);
    if (!fc) return undefined;
    if (fc.band === 'yellow') return 'warning';
    if (fc.band === 'red') return 'error';
    return undefined;
  };

  // Auto-expand "Additional Details" collapse when AI fills address fields
  const aiHasAddressFields = useMemo(() => {
    const addressFields = ['address', 'city', 'state', 'zip'];
    return addressFields.some((f) => !!formContext.fieldConfidences[`customer.${f}`]);
  }, [formContext.fieldConfidences]);

  // Collapse panel key: auto-open when AI fills address fields
  const collapseActiveKeys = aiHasAddressFields ? ['1'] : undefined;

  // Form lifecycle: register/unregister with context
  useEffect(() => {
    setIsFormMounted(true);
    formContext.registerCustomerForm();

    return () => {
      formContext.unregisterCustomerForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (value && value !== selectedCustomer) {
      setSelectedCustomer(value);
      setIsCreatingNew(false);
      setIsEditing(false);
      setShowFormAsTyping(false);
      // Delay populateForm until after React has committed the state updates to the DOM.
      // Without this, form.setFieldsValue() fires before the Form.Item components
      // have re-rendered into "selected customer" mode, so the values don't stick.
      requestAnimationFrame(() => {
        populateForm(value);
      });
      // Mark current AI data as "already applied" so the AI fill effect
      // doesn't re-fire and overwrite the selected customer's data.
      // (Previously set to null, which let the AI effect re-trigger.)
      const currentAiData = formContext.aiState.customerFieldsApproved;
      if (currentAiData) {
        appliedAiDataRef.current = currentAiData;
      }
    }
  }, [value]);

  // Effect to handle AI-approved customer data from context (replaces setTimeout-based prop approach)
  // Guard: only apply once per unique customerFieldsApproved reference to avoid re-running
  // form.setFieldsValue on every context re-render (e.g. when markFieldReviewed fires).
  // Also waits if a suggestion banner is showing — user must decide first.
  useEffect(() => {
    if (!isFormMounted || !formContext.customerFormReady) return;
    // If a candidates list is showing, wait for user to pick or dismiss before filling the form
    if (aiCustomerCandidates.length > 0) return;

    const aiData = formContext.aiState.customerFieldsApproved;
    if (!aiData) return;

    // Skip if we already applied this exact data object
    if (appliedAiDataRef.current === aiData) return;
    appliedAiDataRef.current = aiData;

    // Show the form in create mode
    setIsCreatingNew(true);
    setShowFormAsTyping(false);

    // Use requestAnimationFrame to ensure DOM is fully rendered before setting values
    requestAnimationFrame(() => {
      form.setFieldsValue({
        first_name: aiData.first_name || '',
        last_name: aiData.last_name || '',
        phone: aiData.phone || '',
        email: aiData.email || '',
        company_name: aiData.company_name || '',
        address: aiData.address || '',
        city: aiData.city || '',
        state: aiData.state || '',
        zip: aiData.zip || '',
        notes: aiData.notes || '',
      });

      // Trigger form validation to show populated state
      form.validateFields().catch(() => {});
    });
  }, [isFormMounted, formContext.customerFormReady, formContext.aiState.customerFieldsApproved, aiCustomerCandidates, form]);

  // Auto-save trigger: when phase transitions to 'customer_save', automatically save
  useEffect(() => {
    if (formContext.aiState.orchestratorPhase === 'customer_save') {
      handleSave(false);
    }
  }, [formContext.aiState.orchestratorPhase]);

  const fetchCustomers = async () => {
    try {
      const response = await customerApi.getAll();
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      message.error('Failed to fetch customers');
      setCustomers([]);
    }
  };

  const populateForm = (customer: Customer) => {
    form.setFieldsValue({
      first_name: customer.first_name,
      last_name: customer.last_name,
      phone: customer.phone,
      email: customer.email,
      company_name: customer.company_name,
      address: customer.address,
      city: customer.city,
      state: customer.state,
      zip: customer.zip,
      notes: customer.notes,
      preferred_contact_method: customer.preferred_contact_method,
    });
    setHasChanges(false);
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
  };

  // Process search results when debounced value changes
  // NOTE: No auto-select — let the user pick from the dropdown.
  // Pre-populate form fields as user types for "Add New Customer" flow.
  useEffect(() => {
    // Don't overwrite form fields if user is actively typing in a customer form field
    const activeEl = document.activeElement;
    const formEl = activeEl?.closest?.('.customer-inline-form, [class*="customer"]');
    const isInFormField = formEl && (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA');

    if (debouncedSearchValue.trim().length > 0) {
      setShowFormAsTyping(true);

      // Pre-populate form as user types (for creating new customer)
      if (debouncedSearchValue.trim() && !selectedCustomer && !isInFormField) {
        const nameParts = debouncedSearchValue.trim().split(' ');
        if (nameParts.length >= 2) {
          form.setFieldsValue({
            first_name: nameParts[0],
            last_name: nameParts.slice(1).join(' '),
          });
        } else if (nameParts.length === 1) {
          // Check if it looks like a phone number
          const digitsOnly = debouncedSearchValue.replace(/\D/g, '');
          if (digitsOnly.length >= 3) {
            form.setFieldsValue({
              phone: debouncedSearchValue,
              first_name: '',
              last_name: '',
            });
          } else {
            form.setFieldsValue({
              first_name: nameParts[0],
              last_name: '',
            });
          }
        }
      }
    } else {
      setShowFormAsTyping(false);
      if (!selectedCustomer && !isCreatingNew) {
        form.resetFields();
      }
    }
  }, [debouncedSearchValue, customers, selectedCustomer, isCreatingNew, form, onChange]);

  const handleSelect = (value: string, option: any) => {
    if (value === 'CREATE_NEW') {
      setIsCreatingNew(true);
      setSelectedCustomer(null);
      setIsEditing(false);
      setShowFormAsTyping(false);

      // Pre-populate from current search value
      const nameParts = searchValue.trim().split(' ');
      if (nameParts.length >= 2) {
        form.setFieldsValue({
          first_name: nameParts[0],
          last_name: nameParts.slice(1).join(' '),
        });
      } else if (nameParts.length === 1) {
        const digitsOnly = searchValue.replace(/\D/g, '');
        if (digitsOnly.length >= 3) {
          form.setFieldsValue({
            phone: searchValue,
            first_name: '',
            last_name: '',
          });
        } else {
          form.setFieldsValue({
            first_name: nameParts[0],
            last_name: '',
          });
        }
      }
      setHasChanges(false);
    } else {
      const customer = customers.find(c => c.id === parseInt(value));
      if (customer) {
        setSelectedCustomer(customer);
        setIsCreatingNew(false);
        setIsEditing(false);
        setShowFormAsTyping(false);
        populateForm(customer);
        if (onChange) {
          onChange(customer);
        }
      }
    }
    setSearchValue('');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (selectedCustomer) {
      populateForm(selectedCustomer);
      setIsEditing(false);
    } else {
      setIsCreatingNew(false);
      setShowFormAsTyping(false);
      form.resetFields();
    }
    setHasChanges(false);
  };

  const handleSave = async (updateCustomer: boolean = false) => {
    try {
      // Prevent duplicate submissions
      if (loading) {
        console.warn('Save already in progress, ignoring duplicate request');
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      // Trim all string values to prevent whitespace issues
      const customerData: Omit<Customer, 'id' | 'created_at' | 'updated_at'> = {
        first_name: values.first_name?.trim() || '',
        last_name: values.last_name?.trim() || '',
        phone: values.phone?.trim() || '',
        email: values.email?.trim() || '',
        company_name: values.company_name?.trim() || '',
        address: values.address?.trim() || '',
        city: values.city?.trim() || '',
        state: values.state?.trim() || '',
        zip: values.zip?.trim() || '',
        notes: values.notes?.trim() || '',
        preferred_contact_method: values.preferred_contact_method || 'phone',
      };

      // Validate required fields before API call
      if (!customerData.first_name || !customerData.last_name || !customerData.phone) {
        message.error('First name, last name, and phone are required');
        setLoading(false);
        return;
      }

      // Debug: Log the data being sent to API
      console.log('Creating customer with data:', customerData);

      let savedCustomer: Customer;

      if (isCreatingNew || showFormAsTyping || !selectedCustomer) {
        // Create new customer via API
        const response = await customerApi.create(customerData);
        savedCustomer = response.data;
        message.success('Customer created successfully');

        // Refresh the customer list
        await fetchCustomers();
      } else if (updateCustomer && selectedCustomer?.id) {
        // Update existing customer via API
        const response = await customerApi.update(selectedCustomer.id, customerData);
        savedCustomer = response.data;
        message.success('Customer updated successfully');

        // Refresh the customer list
        await fetchCustomers();
      } else {
        savedCustomer = selectedCustomer!;
      }

      setSelectedCustomer(savedCustomer);
      setIsCreatingNew(false);
      setIsEditing(false);
      setShowFormAsTyping(false);
      setHasChanges(false);

      // Notify parent via prop callback
      if (onChange) {
        onChange(savedCustomer);
      }

      // Notify context for phase transition (triggers vehicle orchestrator)
      formContext.onCustomerSaved(savedCustomer);
    } catch (error: any) {
      console.error('Customer save error:', error);
      console.error('Error response:', error?.response?.data);

      // Extract detailed error message from API response
      if (error?.response?.data) {
        const errorData = error.response.data;

        // Handle validation errors with details
        if (errorData.details && Array.isArray(errorData.details)) {
          const errorMessages = errorData.details.map((detail: any) =>
            `${detail.field}: ${detail.message}`
          ).join(', ');
          message.error(`Validation failed: ${errorMessages}`, 7);
        } else if (errorData.error) {
          // Handle simple error messages
          message.error(errorData.error, 5);
        } else {
          message.error('Failed to save customer. Please check all fields.', 5);
        }
      } else if (error?.message) {
        message.error(error.message, 5);
      } else {
        message.error('Please fill in all required fields correctly', 5);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = () => {
    setHasChanges(true);
  };

  const getFilteredCustomers = (searchText: string = searchValue) => {
    if (!searchText.trim()) return customers;

    const searchLower = searchText.toLowerCase();
    const searchDigits = searchText.replace(/\D/g, '');

    return customers.filter(customer => {
      const customerName = `${customer.first_name} ${customer.last_name}`.toLowerCase();
      const customerCompany = customer.company_name?.toLowerCase() || '';
      const customerPhone = customer.phone?.replace(/\D/g, '') || '';
      const customerEmail = customer.email?.toLowerCase() || '';

      return (
        customerName.includes(searchLower) ||
        customer.first_name.toLowerCase().includes(searchLower) ||
        customer.last_name.toLowerCase().includes(searchLower) ||
        customerCompany.includes(searchLower) ||
        customerEmail.includes(searchLower) ||
        customerPhone.includes(searchDigits) ||
        (customer.phone?.includes(searchText) || false) // For formatted phone searches like "(555) 123"
      );
    });
  };

  // Build ALL customer options with searchText for filtering
  const allCustomerOptions = useMemo(() => {
    const opts = customers.map(customer => ({
      value: customer.id!.toString(),
      searchText: `${customer.first_name} ${customer.last_name} ${customer.phone || ''} ${customer.email || ''} ${customer.company_name || ''}`.toLowerCase(),
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
          <Avatar
            size={24}
            style={{
              backgroundColor: customer.company_name ? '#6366f1' : '#3b82f6',
              color: 'white',
              fontSize: '10px'
            }}
            icon={<UserOutlined />}
          >
            {customer.company_name
              ? customer.company_name.slice(0, 2).toUpperCase()
              : `${customer.first_name.charAt(0)}${customer.last_name.charAt(0)}`
            }
          </Avatar>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', color: '#111827' }}>
              {customer.company_name || `${customer.first_name} ${customer.last_name}`}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {customer.phone}
              {customer.email && ` • ${customer.email}`}
            </div>
          </div>
        </div>
      ),
    }));

    // Always include "Add new customer" option at the top
    opts.unshift({
      value: 'CREATE_NEW',
      searchText: '',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
          <PlusOutlined style={{ color: '#1890ff' }} />
          <span style={{ color: '#1890ff' }}>Add new customer</span>
        </div>
      ),
    });

    return opts;
  }, [customers]);

  const getDisplayName = (customer: Customer) => {
    return customer.company_name || `${customer.first_name} ${customer.last_name}`;
  };

  const showForm = isCreatingNew || isEditing || selectedCustomer || showFormAsTyping;

  const handleAddNewClick = () => {
    setIsCreatingNew(true);
    setSelectedCustomer(null);
    setIsEditing(false);
    setShowFormAsTyping(false);
    setSearchValue('');
    form.resetFields();
    setHasChanges(false);
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Search/Select Customer */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <AutoComplete
          value={searchValue}
          options={allCustomerOptions}
          onSearch={handleSearch}
          onSelect={handleSelect}
          placeholder="Search for a customer or type to add new..."
          style={{ flex: '3 1 200px', minWidth: '200px' }}
          size="large"
          disabled={disabled}
          filterOption={(inputValue, option) => {
            if (!inputValue) return true;
            if (option?.value === 'CREATE_NEW') return true;
            const searchText = (option as any)?.searchText || '';
            return searchText.includes(inputValue.toLowerCase());
          }}
        />
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleAddNewClick}
          disabled={disabled || isCreatingNew}
          style={{
            flex: '1 0 auto',
            minWidth: 'fit-content',
            whiteSpace: 'nowrap'
          }}
        >
          Add New Customer
        </Button>
      </div>

      {/* AI Recommendation List — existing customers found */}
      {aiCustomerCandidates.length > 0 && !selectedCustomer && (
        <Card
          size="small"
          style={{
            marginBottom: '12px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            border: '1px solid #93c5fd',
            borderRadius: '8px',
          }}
        >
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserOutlined style={{ color: '#2563eb', fontSize: '13px' }} />
            <Text strong style={{ color: '#1e40af', fontSize: '13px' }}>
              Existing customers found
            </Text>
            <Tag color="blue" style={{ fontSize: '10px', margin: 0, marginLeft: 'auto', borderRadius: '8px' }}>
              {aiCustomerCandidates.length} match{aiCustomerCandidates.length !== 1 ? 'es' : ''}
            </Tag>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {aiCustomerCandidates.map((candidate, idx) => (
              <div
                key={candidate.customer.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  borderRadius: '6px',
                  border: '1px solid #dbeafe',
                }}
              >
                <Avatar
                  size={32}
                  style={{
                    backgroundColor: candidate.customer.company_name ? '#6366f1' : '#3b82f6',
                    color: 'white',
                    fontSize: '11px',
                    flexShrink: 0,
                  }}
                  icon={<UserOutlined />}
                >
                  {candidate.customer.company_name
                    ? candidate.customer.company_name.slice(0, 2).toUpperCase()
                    : `${candidate.customer.first_name.charAt(0)}${candidate.customer.last_name.charAt(0)}`
                  }
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#1f2937' }}>
                    {candidate.customer.company_name || `${candidate.customer.first_name} ${candidate.customer.last_name}`}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    {candidate.customer.phone}
                    {candidate.customer.email && ` \u2022 ${candidate.customer.email}`}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                    {candidate.phoneMatches && (
                      <Tag color="success" style={{ fontSize: '10px', margin: 0, borderRadius: '8px', lineHeight: '16px' }}>
                        <CheckCircleFilled /> Phone matches
                      </Tag>
                    )}
                    {!candidate.phoneMatches && candidate.matchConfidence >= 0.85 && (
                      <Tag color="blue" style={{ fontSize: '10px', margin: 0, borderRadius: '8px', lineHeight: '16px' }}>
                        Name exact match
                      </Tag>
                    )}
                    {!candidate.phoneMatches && candidate.matchConfidence >= 0.7 && candidate.matchConfidence < 0.85 && (
                      <Tag color="default" style={{ fontSize: '10px', margin: 0, borderRadius: '8px', lineHeight: '16px' }}>
                        Name similar
                      </Tag>
                    )}
                  </div>
                </div>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => onAcceptCandidate?.(candidate.customer)}
                  style={{ fontSize: '11px', borderRadius: '6px', flexShrink: 0 }}
                >
                  Use
                </Button>
              </div>
            ))}
          </div>

          {/* Create new customer instead */}
          <Button
            type="dashed"
            block
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              onDismissCandidates?.();
              setIsCreatingNew(true);
            }}
            style={{
              marginTop: '8px',
              fontSize: '12px',
              borderRadius: '6px',
              color: '#2563eb',
              borderColor: '#93c5fd',
            }}
          >
            Create new customer instead
          </Button>
        </Card>
      )}

      {/* Selected Customer Display */}
      {selectedCustomer && !isEditing && !isCreatingNew && (
        <Card
          size="small"
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            marginBottom: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                style={{
                  backgroundColor: selectedCustomer.company_name ? '#6366f1' : '#3b82f6',
                  color: 'white'
                }}
                icon={<UserOutlined />}
              >
                {selectedCustomer.company_name
                  ? selectedCustomer.company_name.slice(0, 2).toUpperCase()
                  : `${selectedCustomer.first_name.charAt(0)}${selectedCustomer.last_name.charAt(0)}`
                }
              </Avatar>
              <div>
                <Text strong style={{ color: '#0369a1' }}>
                  {getDisplayName(selectedCustomer)}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {selectedCustomer.phone}
                  {selectedCustomer.email && ` • ${selectedCustomer.email}`}
                </Text>
              </div>
            </div>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={handleEdit}
              disabled={disabled}
            >
              Edit
            </Button>
          </div>
        </Card>
      )}

      {/* Customer Form */}
      <Card
        title={
          isCreatingNew
            ? "New Customer Information"
            : isEditing
              ? "Edit Customer Information"
              : showFormAsTyping
                ? "Creating Customer..."
                : "Customer Information"
        }
        size="small"
        style={{
          marginBottom: '16px',
          display: showForm ? 'block' : 'none'
        }}
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleFieldChange}
            disabled={disabled || (!!selectedCustomer && !isEditing)}
          >
            {/* Required Fields */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span>First Name<AnimatePresence>{fieldConf('first_name') && <AIFieldConfidenceBadge confidence={fieldConf('first_name')!.confidence} band={fieldConf('first_name')!.band} />}</AnimatePresence></span>}
                  name="first_name"
                  rules={[{ required: true, message: 'First name is required' }]}
                >
                  <Input
                    placeholder="First name"
                    style={{ width: '100%', ...confStyle('first_name') }}
                    status={confStatus('first_name')}
                    onFocus={() => {
                      formContext.markFieldReviewed('customer.first_name');
                      formContext.setFocusedField({ path: 'customer.first_name', current_value: form.getFieldValue('first_name') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span>Last Name<AnimatePresence>{fieldConf('last_name') && <AIFieldConfidenceBadge confidence={fieldConf('last_name')!.confidence} band={fieldConf('last_name')!.band} />}</AnimatePresence></span>}
                  name="last_name"
                  rules={[{ required: true, message: 'Last name is required' }]}
                >
                  <Input
                    placeholder="Last name"
                    style={{ width: '100%', ...confStyle('last_name') }}
                    status={confStatus('last_name')}
                    onFocus={() => {
                      formContext.markFieldReviewed('customer.last_name');
                      formContext.setFocusedField({ path: 'customer.last_name', current_value: form.getFieldValue('last_name') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span>Phone<AnimatePresence>{fieldConf('phone') && <AIFieldConfidenceBadge confidence={fieldConf('phone')!.confidence} band={fieldConf('phone')!.band} />}</AnimatePresence></span>}
                  name="phone"
                  rules={[{ required: true, message: 'Phone number is required' }]}
                >
                  <Input
                    placeholder="(555) 123-4567"
                    prefix={<PhoneOutlined style={{ color: '#9ca3af' }} />}
                    style={{ width: '100%', ...confStyle('phone') }}
                    status={confStatus('phone')}
                    onFocus={() => {
                      formContext.markFieldReviewed('customer.phone');
                      formContext.setFocusedField({ path: 'customer.phone', current_value: form.getFieldValue('phone') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span>Email<AnimatePresence>{fieldConf('email') && <AIFieldConfidenceBadge confidence={fieldConf('email')!.confidence} band={fieldConf('email')!.band} />}</AnimatePresence></span>}
                  name="email"
                  rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                >
                  <Input
                    placeholder="customer@email.com"
                    prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                    style={{ width: '100%', ...confStyle('email') }}
                    status={confStatus('email')}
                    onFocus={() => {
                      formContext.markFieldReviewed('customer.email');
                      formContext.setFocusedField({ path: 'customer.email', current_value: form.getFieldValue('email') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Collapsible Additional Details — auto-expands when AI fills address fields */}
            <Collapse
              ghost
              size="small"
              activeKey={collapseActiveKeys}
              items={[
                {
                  key: '1',
                  label: 'Additional Details',
                  children: (
                    <>
                      <Form.Item
                        label="Company Name"
                        name="company_name"
                      >
                        <Input
                          placeholder="Company name"
                          onFocus={() => {
                            formContext.markFieldReviewed('customer.company_name');
                            formContext.setFocusedField({ path: 'customer.company_name', current_value: form.getFieldValue('company_name') });
                          }}
                          onBlur={() => formContext.setFocusedField(null)}
                        />
                      </Form.Item>

                      <Form.Item
                        label={<span>Street Address<AnimatePresence>{fieldConf('address') && <AIFieldConfidenceBadge confidence={fieldConf('address')!.confidence} band={fieldConf('address')!.band} />}</AnimatePresence></span>}
                        name="address"
                      >
                        <Input
                          placeholder="123 Main Street"
                          style={{ width: '100%', ...confStyle('address') }}
                          status={confStatus('address')}
                          onFocus={() => {
                            formContext.markFieldReviewed('customer.address');
                            formContext.setFocusedField({ path: 'customer.address', current_value: form.getFieldValue('address') });
                          }}
                          onBlur={() => formContext.setFocusedField(null)}
                        />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            label={<span>City<AnimatePresence>{fieldConf('city') && <AIFieldConfidenceBadge confidence={fieldConf('city')!.confidence} band={fieldConf('city')!.band} />}</AnimatePresence></span>}
                            name="city"
                          >
                            <Input
                              placeholder="City"
                              style={{ width: '100%', ...confStyle('city') }}
                              status={confStatus('city')}
                              onFocus={() => {
                                formContext.markFieldReviewed('customer.city');
                                formContext.setFocusedField({ path: 'customer.city', current_value: form.getFieldValue('city') });
                              }}
                              onBlur={() => formContext.setFocusedField(null)}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Row gutter={8}>
                            <Col xs={12}>
                              <Form.Item
                                label={<span>State<AnimatePresence>{fieldConf('state') && <AIFieldConfidenceBadge confidence={fieldConf('state')!.confidence} band={fieldConf('state')!.band} />}</AnimatePresence></span>}
                                name="state"
                              >
                                <Input
                                  placeholder="CA"
                                  style={{ width: '100%', ...confStyle('state') }}
                                  status={confStatus('state')}
                                  onFocus={() => {
                                    formContext.markFieldReviewed('customer.state');
                                    formContext.setFocusedField({ path: 'customer.state', current_value: form.getFieldValue('state') });
                                  }}
                                  onBlur={() => formContext.setFocusedField(null)}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12}>
                              <Form.Item
                                label={<span>ZIP Code<AnimatePresence>{fieldConf('zip') && <AIFieldConfidenceBadge confidence={fieldConf('zip')!.confidence} band={fieldConf('zip')!.band} />}</AnimatePresence></span>}
                                name="zip"
                              >
                                <Input
                                  placeholder="12345"
                                  style={{ width: '100%', ...confStyle('zip') }}
                                  status={confStatus('zip')}
                                  onFocus={() => {
                                    formContext.markFieldReviewed('customer.zip');
                                    formContext.setFocusedField({ path: 'customer.zip', current_value: form.getFieldValue('zip') });
                                  }}
                                  onBlur={() => formContext.setFocusedField(null)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                      <Form.Item
                        label="Preferred Contact Method"
                        name="preferred_contact_method"
                        style={{ marginTop: '16px' }}
                      >
                        <Select placeholder="Select preferred contact method" style={{ width: '100%' }}>
                          <Select.Option value="phone">Phone</Select.Option>
                          <Select.Option value="email">Email</Select.Option>
                          <Select.Option value="sms">SMS</Select.Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Notes"
                        name="notes"
                      >
                        <TextArea
                          placeholder="Customer notes, preferences, special instructions..."
                          rows={2}
                          onFocus={() => formContext.markFieldReviewed('customer.notes')}
                        />
                      </Form.Item>
                    </>
                  ),
                }
              ]}
            />

            {/* Save/Cancel buttons at bottom of form for mobile usability */}
            {(isCreatingNew || isEditing || showFormAsTyping) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                <Button size="middle" onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                {isEditing && hasChanges && (
                  <Button
                    type="default"
                    size="middle"
                    onClick={() => handleSave(false)}
                    loading={loading}
                  >
                    Use Changes
                  </Button>
                )}
                <Button
                  type="primary"
                  size="middle"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave(showFormAsTyping ? false : isEditing)}
                  loading={loading}
                >
                  {isCreatingNew || showFormAsTyping ? 'Add Customer' : isEditing ? 'Update Customer' : 'Save'}
                </Button>
              </div>
            )}
          </Form>
        </Card>

      {/* Helper Text */}
      {!selectedCustomer && !isCreatingNew && (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Start typing to search for existing customers or create a new one
        </Text>
      )}
    </div>
  );
};

export default CustomerInlineForm;